import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import StoreInventoryStatusModal from "src/components/modals/StoreInventoryStatusModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  useDeleteInventory,
  useGetInventoryRequests,
  useUpdateInventoryStatus,
} from "../../api-inventories";
import useInventoryTableColumns from "./useInventoriesTableColumns";

const InventoryList = () => {
  useSetActiveTab(TAB_NAMES.INVENTORY_REQUEST);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const [inventoryStatusModal, setInventoryStatusModal] = useState(false);
  const {
    isLoading,
    error,
    data,
    mutate: updateInventoryData,
    isValidating,
  } = useGetInventoryRequests({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(updateInventoryData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteInventory,
  } = useDeleteInventory();

  const {
    isLoading: isInventoryStatusLoading,
    isSuccess: isInventorySucceed,
    error: isInventoryFailed,
    execute: updateInventoryStatus,
  } = useUpdateInventoryStatus();

  const inventoryData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useInventoryTableColumns({
    onDelete: (id: number) => {
      // setIdToOperate(id);
      // setIsDeleteModalOpen(true);
    },
    onStatusClick: (id: number) => {
      setIdToOperate(id);
      setInventoryStatusModal(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateInventoryData();
      setIsDeleteModalOpen(false);
      successNotification("Inventory Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateInventoryData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  useEffect(() => {
    if (isInventorySucceed) {
      updateInventoryData();
      successNotification("Status updated.");
      setInventoryStatusModal(false);
    } else if (isInventoryFailed) {
      errorNotification("Something went wrong.");
    }
  }, [
    errorNotification,
    isInventoryFailed,
    isInventorySucceed,
    successNotification,
    updateInventoryData,
  ]);

  const handleStatusUpdate = () => {
    const body = {
      reqId: idToOperate,
      status: 2,
    };
    updateInventoryStatus(body);
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteInventory(idToOperate)}
      />
      <StoreInventoryStatusModal
        isOpen={inventoryStatusModal}
        onClose={() => setInventoryStatusModal(false)}
        isLoading={isInventoryStatusLoading}
        onSubmit={handleStatusUpdate}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Inventory Request"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(inventoryData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Inventory",
            onClick: () => navigate("/store/vendor/inventory/all/create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={inventoryData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default InventoryList;
